import React, { useState, useRef, useEffect } from 'react';

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  textClassName?: string;
  buttonClassName?: string;
  showMoreText?: string;
  showLessText?: string;
  lineHeight?: number; // in px
}

/**
 * ExpandableText component that shows/hides text content when it exceeds a certain number of lines
 * Uses actual element height measurement instead of counting newlines
 */
const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  maxLines = 5,
  className = '',
  textClassName = '',
  buttonClassName = 'mt-1 text-blue-600 hover:text-blue-800 text-sm font-medium',
  showMoreText = 'แสดงเพิ่มเติม',
  showLessText = 'แสดงน้อยลง',
  lineHeight = 24, // default line height in pixels
}) => {
  const [showFullText, setShowFullText] = useState(false);
  const [exceedsMaxLines, setExceedsMaxLines] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  
  // Mark component as mounted
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  
  // Use useEffect to measure the actual height of the text content after render
  useEffect(() => {
    // Skip measurement if text is empty or component is not mounted
    if (!text || !isMounted) {
      setExceedsMaxLines(false);
      return;
    }
    
    const checkHeight = () => {
      if (!textRef.current) return;
      
      try {
        const textElement = textRef.current;
        
        // Store original styles
        const originalMaxHeight = textElement.style.maxHeight;
        const originalWebkitLineClamp = textElement.style.webkitLineClamp;
        const originalOverflow = textElement.style.overflow;
        const originalDisplay = textElement.style.display;
        const originalWebkitBoxOrient = textElement.style.webkitBoxOrient;
        
        // Remove constraints to measure full height
        textElement.style.maxHeight = 'none';
        textElement.style.webkitLineClamp = 'unset';
        textElement.style.overflow = 'visible';
        textElement.style.display = 'block';
        
        // Get the full height
        const fullHeight = textElement.scrollHeight;
        
        // Calculate height of maxLines
        const maxHeight = maxLines * lineHeight;
        
        // Determine if content exceeds maxLines
        const exceeds = fullHeight > maxHeight;
        setExceedsMaxLines(exceeds);
        
        // Restore original styles
        textElement.style.maxHeight = originalMaxHeight;
        textElement.style.webkitLineClamp = originalWebkitLineClamp;
        textElement.style.overflow = originalOverflow;
        textElement.style.display = originalDisplay;
        textElement.style.webkitBoxOrient = originalWebkitBoxOrient;
      } catch (error) {
        console.error("Error measuring text height:", error);
        setExceedsMaxLines(false);
      }
    };

    // Small delay to ensure the DOM is fully rendered
    const timer = setTimeout(() => {
      checkHeight();
    }, 0);
    
    // Also check on window resize as text flow might change
    window.addEventListener('resize', checkHeight);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkHeight);
    };
  }, [maxLines, lineHeight, text, isMounted]);
  
  // Render a dual-purpose div that both displays text and serves for measurement
  return (
    <div className={`relative ${className}`}>
      <div 
        ref={textRef}
        className={`${textClassName} break-words overflow-hidden transition-all duration-300`}
        style={{ 
          maxHeight: showFullText || !exceedsMaxLines ? 'none' : `${maxLines * lineHeight}px`,
          WebkitLineClamp: showFullText || !exceedsMaxLines ? 'unset' : maxLines,
          display: exceedsMaxLines ? '-webkit-box' : 'block',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {text}
      </div>
      {exceedsMaxLines && (
        <button
          onClick={() => setShowFullText(!showFullText)}
          className={buttonClassName}
        >
          {showFullText ? showLessText : showMoreText}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;